import { oktaTokenManager } from './oktaTokenManager.js';
import { sessionManager } from './sessionManager.js';
import { CONFIG } from '../config/config.js';
import { redirectToLogin, isOnLoginPage } from './navigationUtils.js';
import { appInsights } from './appInsights.js';

class ApiClient {
    constructor() {
        this.baseURL = CONFIG.API_BASE_URL;
        this.onUnauthorized = null;
        this.maxRetries = 3;
        this.timeout = 30000;

        this.validateHttpsUrl(this.baseURL);
    }

    setOnUnauthorized(callback) {
        this.onUnauthorized = callback;
    }

    validateHttpsUrl(url) {
        if (!url || typeof url !== 'string') {
            throw new Error('Invalid URL: URL must be a non-empty string');
        }

        // Same-origin relative paths (e.g. "/ClientFileUploadPortal") are proxied
        // server-side via IIS ARR, so they inherit the page's HTTPS connection.
        if (url.startsWith('/')) {
            return;
        }

        if (!url.startsWith('https://')) {
            throw new Error(`Invalid URL protocol: Expected HTTPS but got ${url}. All API calls must use HTTPS for security.`);
        }
    }

    validateEndpoint(endpoint) {
        if (!endpoint || typeof endpoint !== 'string') {
            throw new Error('Invalid endpoint parameter');
        }

        // Prevent path traversal attacks
        if (endpoint.includes('..') || endpoint.includes('\\')) {
            throw new Error('Invalid endpoint: path traversal detected');
        }

        return endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    }

    async request(endpoint, options = {}) {
        const validatedEndpoint = this.validateEndpoint(endpoint);
        const url = `${this.baseURL}${validatedEndpoint}`;

        const token = await oktaTokenManager.getValidAccessToken();

        // Don't set Content-Type for FormData - browser will set it with boundary
        const isFormData = options.body instanceof FormData;

        const config = {
            headers: {
                ...(!isFormData && { 'Content-Type': 'application/json' }),
                'X-Requested-With': 'XMLHttpRequest', // CSRF protection
                'Cache-Control': 'no-cache', // Prevent caching of sensitive data
                'Pragma': 'no-cache',
                ...options.headers,
            },
            credentials: 'same-origin', // Include cookies for CSRF protection
            timeout: this.timeout,
            ...options,
        };

        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }

        try {
            const response = await fetch(url, config);

            if (response.status === 401) {
                appInsights.trackEvent('UnauthorizedRequest', { endpoint: validatedEndpoint });

                // Clear tokens and session
                await oktaTokenManager.clearTokens();
                sessionManager.clearSession();

                // Use callback to handle unauthorized access
                if (this.onUnauthorized) {
                    this.onUnauthorized();
                } else if (!isOnLoginPage()) {
                    // Fallback: only redirect if not already on login page
                    redirectToLogin('401_unauthorized');
                }

                const error = new Error('Unauthorized - 401');
                error.status = 401;
                error.response = response;
                throw error;
            }

            if (!response.ok) {
                const contentType = response.headers.get('content-type');
                let errorMessage = `HTTP error! status: ${response.status}`;

                try {
                    if (contentType && contentType.includes('application/json')) {
                        const errorData = await response.json();
                        if (errorData.message) {
                            errorMessage = errorData.message;
                        }
                    } else {
                        const errorText = await response.text();
                        errorMessage = `HTTP error! status: ${response.status}, message: ${errorText}`;
                    }
                } catch (parseError) {
                    appInsights.trackException(parseError, { component: 'ApiClient', action: 'parseErrorResponse' });
                }

                const error = new Error(errorMessage);
                error.status = response.status;
                error.response = {
                    ...response,
                    data: {
                        message: errorMessage || null
                    }
                };
                throw error;
            }

            const contentType = response.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
                const data = await response.json();

                // Basic XSS protection - sanitize response data
                return this.sanitizeResponse(data);
            }

            return response;
        } catch (error) {
            appInsights.trackException(error, { component: 'ApiClient', endpoint: validatedEndpoint });

            // Don't expose sensitive error details to the client
            if (error.status === 401) {
                throw error; // Re-throw authorization errors
            }

            // If we already have a meaningful error message, preserve it
            if (error.message && error.message !== 'Request failed. Please try again.') {
                throw error;
            }
            throw new Error('Request failed. Please try again.');
        }
    }

    // Basic XSS protection for response data
    sanitizeResponse(data) {
        if (typeof data === 'string') {
            return data.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
        }

        if (Array.isArray(data)) {
            return data.map(item => this.sanitizeResponse(item));
        }

        if (typeof data === 'object' && data !== null) {
            const sanitized = {};
            for (const [key, value] of Object.entries(data)) {
                sanitized[key] = this.sanitizeResponse(value);
            }
            return sanitized;
        }

        return data;
    }

    async get(endpoint, options = {}) {
        return this.request(endpoint, { method: 'GET', ...options });
    }

    async post(endpoint, data, options = {}) {
        return this.request(endpoint, {
            method: 'POST',
            body: JSON.stringify(data),
            ...options,
        });
    }

    async put(endpoint, data, options = {}) {
        return this.request(endpoint, {
            method: 'PUT',
            body: JSON.stringify(data),
            ...options,
        });
    }

    async delete(endpoint, options = {}) {
        return this.request(endpoint, { method: 'DELETE', ...options });
    }
}

export const apiClient = new ApiClient();